import Container from 'components/container/container';
import LegalPost from 'components/legal/legal-post';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { BILLING_MAILTO, LIBUM_URL, SUPPORT_MAILTO } from 'shared/constants/globals';
import { LEGAL_DOCS } from 'shared/data/legal-docs';

const DOC = LEGAL_DOCS.find((d) => d.id === 'sla')!;

export default function SLA() {
  const title = DOC.title;
  const description = DOC.description;
  const keywords = DOC.keywords.join(',');
  const url = `${LIBUM_URL}/legal/${DOC.id}`;

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return null;
  }

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="legal"
      breadcrumbs={[
        { name: 'Legal', url: `${LIBUM_URL}/legal` },
        { name: DOC.title, url },
      ]}
    >
      <div style={{ width: '100%' }}>
        <LegalPost document={DOC}>
          <h1 className="anchor" id="general">
            <Link href="#general">1. General</Link>
          </h1>
          <p>
            Support will be provided to authorized Users in accordance with the terms of this document. Capitalized
            terms not otherwise defined in this document will have the respective meanings assigned to them in the
            applicable Master Services Agreement between Libum and Customer.
          </p>
          <p>
            Requests for Support may be made through any of the channels referenced in the section below titled “Support
            Requests.” Customer agrees to provide Libum with reasonable information and assistance to facilitate
            Libum&apos;s performance of Support, including, without limitation, a clear description of the issue,
            related configuration and log files, and cooperation to reproduce errors. Customer&apos;s sole and exclusive
            remedy for any alleged failure by Libum to provide Support with reasonable skill, care, and diligence
            following a Support request shall be re-performance of the applicable Support.
          </p>
          <h2 className="anchor" id="definitions">
            <Link href="#definitions">1.1 Definitions</Link>
          </h2>
          <p>
            “<b>Available</b>” means with respect to each Software, that the Software being used by Customer is
            available for access; “<b>Availability</b>” has a correlative meaning. Availability is assessed from the
            point where the Software is made available from the Platform and measured in minutes over the course of each
            calendar month during the Order Term. Customer may request Availability information by submitting a Support
            request.
          </p>
          <p>
            “<b>Exceptions</b>” means any of: (a) Customer&apos;s breach of this Agreement or an Order (b)
            Customer&apos;s failure to configure and use the Software in accordance with the Documentation; (c) failures
            of, or issues with, Customer&apos;s Environment; (d) Force Majeure Events; or (e) maintenance during a
            window for which Libum provides notice by email or through the Platform in advance.
          </p>
          <h2 className="anchor" id="availability">
            <Link href="#availability">1.2 Availability</Link>
          </h2>
          <p>
            Libum commits to make the Platform Available at least 99.8% of the time, exclusive of any time the Platform
            is not Available as a result of one or more Exceptions (the “Availability Standard”). If the actual
            Available of the applicable Software is less than the Availability Standard in any monthly billing cycle,
            Customer will be eligible to receive Service Credit as described below.
          </p>
          <p>
            <table>
              <thead>
                <tr>
                  <th>Monthly Uptime Percentage</th>
                  <th>Service Credit Percentage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Less than 99.8% but equal to or greater than 99.0%</td>
                  <td>10%</td>
                </tr>
                <tr>
                  <td>Less than 99.0% but equal to or greater than 95%</td>
                  <td>30%</td>
                </tr>
                <tr>
                  <td>Less than 95.0%</td>
                  <td>50%</td>
                </tr>
              </tbody>
            </table>
          </p>
          <h1 className="anchor" id="access-to-self-help-materials">
            <Link href="#access-to-self-help-materials">2. Access to Self-Help Materials</Link>
          </h1>
          <p>
            Libum has more than one option available for Customers seeking self-help in addressing questions about the
            Services. Customers are responsible for evaluating any guidance found through these self-help options, which
            do not constitute Support. The self-help options are:
            <ul>
              <li>
                <span className="underline">Documentation</span> - Our Documentation library offers considerable
                information for using and configuring our Services. Available at:{' '}
                <Link href="https://docs.libum.io/">https://docs.libum.io/</Link>
              </li>
              <li>
                <span className="underline">Libum Discord Community</span> - The Discord Community program, delivered
                external from the Services, is a network built by the Libum team for users of Libum products to help
                each other discover answers to questions or to share tips and tricks. This means that the information in
                the Discord Community is not provided by Libum as part of Support. The Discord Community can be accessed
                at: <Link href="https://discord.gg/libum">https://discord.gg/libum</Link>
              </li>
            </ul>
          </p>
          <h1 className="anchor" id="support-plan">
            <Link href="#support-plan">3. Support Plan</Link>
          </h1>
          <p>
            <b>Support Requests</b>
            <br />
            <br />
            Customers may leverage <Link href={SUPPORT_MAILTO}>support@libum.io</Link> to contact Libum support. Please
            see the section below titled “Contacting Libum Support” for details on hours and initial response times.
          </p>
          <h1 className="anchor" id="contact-libum-support">
            <Link href="#contact-libum-support">4. Contact Libum Support</Link>
          </h1>
          <p>
            As used in this Section:
            <ul>
              <li>
                &quot;<b>Business Hours</b>&quot; means 9:00 AM - 4:00 PM (CDT/CST) Monday through Friday, excluding
                holidays. Current holidays observed by Libum align with the federal holidays published by the U.S.
                Department of Commerce.
              </li>
              <li>
                &quot;<b>Incident</b>&quot; means a failure of the Service to perform in material conformance with the
                Master Services Agreement (or “MSA”), applicable Order, and Documentation.
              </li>
              <li>
                &quot;<b>Resolution</b>&quot; means either Libum has: (a) corrected the Incident that prompted a
                particular Support request so the Services perform in material conformance with the MSA, applicable
                Order, and Documentation, or (b) determined the reported Incident was actually the result of an
                Exception.
              </li>
              <li>
                &quot;<b>Workaround</b>&quot; means a configuration change, manual procedure, or other workaround
                designed to regain intended or previously functioning features and functionality, but that does not
                provide a complete Resolution.
              </li>
            </ul>
          </p>
          <p>In the event of an Incident, Libum will respond to associated Support requests as follows:</p>
          <p>
            <table className="support">
              <thead>
                <tr>
                  <th>Impact</th>
                  <th>Support</th>
                  <th>Additional Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <span className="underline">Business Critical Incident</span>
                    <br />
                    Issues involving (a) total failure or severe degradation of a Service, or (b) Customer is unable to
                    access the Service.
                  </td>
                  <td>Response within 2 hours (24x7)</td>
                  <td>
                    Libum’s support engineers will work on the Incident 24x7 to provide a Workaround or Resolution.
                    Libum will provide regular status updates as agreed by the Parties. Business Critical Incidents will
                    be escalated to senior engineering as appropriate.
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="underline">Degraded Service Incident</span>
                    <br />
                    Issues involving partial failure or mild degradation of a Service. Customer is able to access some
                    but not all Service features.
                  </td>
                  <td>Initial response within 12 Business Hours</td>
                  <td>
                    Libum support engineers will work on the Incident during Business Hours to provide a Workaround or
                    Resolution.
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="underline">General Issue</span>
                    <br />
                    All other issues not amounting to Business Critical or Degraded Service. These include, but are not
                    limited to non-critical technical issues, product “how-to” questions, implementation questions, and
                    feature requests.
                  </td>
                  <td>Initial response within 48 Business Hours</td>
                  <td>
                    Libum support engineers will work during Business Hours to (i) provide a Workaround or Resolution
                    for Incidents; and (ii) respond to other issues and queries.
                  </td>
                </tr>
              </tbody>
            </table>
          </p>
          <h1 className="anchor" id="miscellaneous-terms">
            <Link href="#miscellaneous-terms">Miscellaneous Terms</Link>
          </h1>
          <p>
            <b>Exclusions</b> - Support Plans do not include any of the following:
            <ul>
              <li>
                <span className="underline">Billing questions</span> - Billing questions should be emailed to{' '}
                <Link href={BILLING_MAILTO}>billing@libum.io</Link> or addressed with your applicable customer success
                representative.
              </li>
            </ul>
            <b>Modifications</b> - The Support Plans may be modified from time to time by Libum, provided that the level
            of support will not materially decrease during the applicable Order Term.
          </p>
        </LegalPost>
      </div>
    </Container>
  );
}
